/**
 * Кеш декодированных аудиодорожек (docs/DUBBING.md, «Ключевое техническое
 * ограничение»): M&E декодируется целиком, а это десятки-сотни мегабайт
 * float32 на минуты звука. Поэтому кеш маленький и с явным вытеснением.
 *
 * Ключ — `${trackId}@${sampleRate}`: AudioBuffer, декодированный под один
 * контекст, на контексте с другой частотой дискретизации звучал бы не в той
 * скорости.
 */

/** Дорожек в кеше одновременно: M&E для превью + dialogue для guide. */
const MAX_ENTRIES = 2

type Entry = { key: string; buffer: AudioBuffer; usedAt: number }

const cache = new Map<string, Entry>()
const inflight = new Map<string, Promise<AudioBuffer>>()

function keyFor(sampleRate: number, trackId: string): string {
  return `${trackId}@${Math.round(sampleRate)}`
}

function touch(entry: Entry): AudioBuffer {
  entry.usedAt = Date.now()
  return entry.buffer
}

function evict(): void {
  while (cache.size > MAX_ENTRIES) {
    let oldest: Entry | null = null
    for (const entry of cache.values()) {
      if (!oldest || entry.usedAt < oldest.usedAt) oldest = entry
    }
    if (!oldest) return
    cache.delete(oldest.key)
  }
}

/** Уже декодированный буфер или null — синхронно, без сети. */
export function peekTrackBuffer(ctx: BaseAudioContext, trackId: string): AudioBuffer | null {
  const entry = cache.get(keyFor(ctx.sampleRate, trackId))
  return entry ? touch(entry) : null
}

/**
 * Скачивает и декодирует дорожку. Параллельные вызовы для одного трека
 * разделяют один запрос — превью и guide не качают файл дважды.
 */
export function loadTrackBuffer(
  ctx: BaseAudioContext,
  trackId: string,
  url: string,
  signal?: AbortSignal,
): Promise<AudioBuffer> {
  const key = keyFor(ctx.sampleRate, trackId)
  const cached = cache.get(key)
  if (cached) return Promise.resolve(touch(cached))
  const running = inflight.get(key)
  if (running) return running

  const task = (async (): Promise<AudioBuffer> => {
    const res = await fetch(url, signal ? { signal } : undefined)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const raw = await res.arrayBuffer()
    const buffer = await ctx.decodeAudioData(raw)
    cache.set(key, { key, buffer, usedAt: Date.now() })
    evict()
    return buffer
  })()

  inflight.set(key, task)
  void task.catch(() => undefined).finally(() => {
    if (inflight.get(key) === task) inflight.delete(key)
  })
  return task
}

/** Выбросить всё, что не относится к перечисленным дорожкам. */
export function retainTrackBuffers(trackIds: Iterable<string>): void {
  const keep = new Set(trackIds)
  for (const [key, entry] of [...cache.entries()]) {
    const id = entry.key.slice(0, entry.key.lastIndexOf('@'))
    if (!keep.has(id)) cache.delete(key)
  }
}

export function dropTrackBuffer(trackId: string): void {
  for (const [key] of [...cache.entries()]) {
    if (key.slice(0, key.lastIndexOf('@')) === trackId) cache.delete(key)
  }
}

export function clearTrackBuffers(): void {
  cache.clear()
}

/** Оценка занятой памяти в байтах — для диагностики в UI. */
export function trackBufferBytes(): number {
  let total = 0
  for (const entry of cache.values()) {
    total += entry.buffer.length * entry.buffer.numberOfChannels * 4
  }
  return total
}
